import { useMemo } from 'react';
import { DRAWER_TEXT } from '../components/otp/otp-text-constants';
import { getOTPButtonsConfig, getOTPHeaderText } from '../components/otp/otp-drawer-config';
import { getCustomerInfo } from '../utils/general';
import { OTP_OPTIONS } from '../utils/consts';

interface OTPOption {
  channel: string;
  // Add other properties as needed
}

interface ButtonConfig {
  inline: {
    text: string;
    channel: string;
  };
  separate?: {
    text: string;
    channel: string;
  };
}

interface UseOTPDrawerContentParams {
  displayPhoneNumber?: string | null;
  lastUsedOtpChannel?: string;
  hasPhoneFromResponse?: boolean;
  otpOptions?: OTPOption[];
}

interface DrawerContentState {
  drawerTexts: {
    title: string;
    header: string;
    verificationCode: string;
    verifyButton: string;
    cancelButton: string;
    refreshCode: string;
  };
  contactMethodToUse: string;
  buttonsConfig: ButtonConfig;
  isOTPOptionsReadyToDisplay: boolean;
}

/**
 * Custom hook to determine the drawer content logic and state
 * Centralizes all the decision-making logic for what content to show
 * Enhanced with better data validation to prevent content flashing
 */
export const useOTPDrawerTextContent = ({
  displayPhoneNumber,
  lastUsedOtpChannel,
  hasPhoneFromResponse = false,
  otpOptions
}: UseOTPDrawerContentParams): DrawerContentState => {

  // Get user email directly from the hook
  const { userEmail } = getCustomerInfo();

  // Enhanced data readiness check
  const isOTPOptionsReadyToDisplay = useMemo(() => {
    // We need at least one of these to be available to show meaningful content
    return !!(
      displayPhoneNumber
      || userEmail
      || lastUsedOtpChannel
      || (otpOptions && otpOptions.length > 0)
    );
  }, [displayPhoneNumber, userEmail, lastUsedOtpChannel, otpOptions]);

  // Determine contact method to display with better fallbacks
  const contactMethodToUse = useMemo(() => {
    if (!isOTPOptionsReadyToDisplay) return 'Loading contact information...';

    // If we have lastUsedOtpChannel, prioritize showing the contact method for that channel
    if (lastUsedOtpChannel === OTP_OPTIONS.EMAIL) {
      // If email was used, show email if available and not empty, otherwise fallback
      return (userEmail && userEmail.trim()) ? userEmail : 'No contact method available';
    }
    if (lastUsedOtpChannel === OTP_OPTIONS.SMS || lastUsedOtpChannel === OTP_OPTIONS.PHONE) {
      // If SMS/phone was used, show phone if available and not empty
      if (displayPhoneNumber && displayPhoneNumber.trim()) {
        return displayPhoneNumber;
      }
      // If no valid display phone number, fall back to email if available
      if (userEmail && userEmail.trim()) {
        return userEmail;
      }
      // If neither phone nor email are available, show fallback
      return 'No contact method available';
    }

    // Fallback to original logic if no lastUsedOtpChannel
    return displayPhoneNumber || userEmail || 'No contact method available';
  }, [displayPhoneNumber, userEmail, lastUsedOtpChannel, isOTPOptionsReadyToDisplay]);

  // Get drawer text constants with data readiness awareness
  const drawerTexts = useMemo(() => ({
    title: DRAWER_TEXT.TITLE,
    header: isOTPOptionsReadyToDisplay
      ? getOTPHeaderText(lastUsedOtpChannel, !!hasPhoneFromResponse)
      : 'Preparing verification...',
    verificationCode: DRAWER_TEXT.VERIFICATION_CODE,
    verifyButton: DRAWER_TEXT.VERIFY_BUTTON,
    cancelButton: DRAWER_TEXT.CANCEL_BUTTON,
    refreshCode: DRAWER_TEXT.REFRESH_CODE
  }), [lastUsedOtpChannel, hasPhoneFromResponse, isOTPOptionsReadyToDisplay]);

  // Get button configuration with defensive programming
  const buttonsConfig = useMemo(() => {
    if (!isOTPOptionsReadyToDisplay) {
      // Return a safe default configuration while data is loading
      return {
        inline: {
          text: 'Loading...',
          channel: ''
        }
      };
    }

    // Treat undefined otpOptions as empty array
    const availableOtpOptions = (otpOptions || []).map((option) => option.channel);
    return getOTPButtonsConfig(
      lastUsedOtpChannel,
      availableOtpOptions,
      hasPhoneFromResponse ?? false
    );
  }, [lastUsedOtpChannel, otpOptions, hasPhoneFromResponse, isOTPOptionsReadyToDisplay]);

  return {
    drawerTexts,
    contactMethodToUse,
    buttonsConfig,
    isOTPOptionsReadyToDisplay
  };
};
